import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import {
  ChevronRight, Shield, Users, RefreshCw, ArrowUpCircle, ArrowDownCircle,
  Star, Zap, ShoppingBag, User, AlertCircle, Clock
} from 'lucide-react';
import { smartgateAPI } from '../../api';

// ─── Transaction type display ──────────────────────────────────────────────────
const TXN_META = {
  WELCOME_BONUS:   { label: 'Welcome Bonus',     icon: Star,         color: 'text-amber-600',  bg: 'bg-amber-50' },
  PURCHASE_CREDIT: { label: 'Purchase Reward',   icon: ShoppingBag,  color: 'text-green-600',  bg: 'bg-green-50' },
  MANUAL_CREDIT:   { label: 'Manual Credit',     icon: ArrowUpCircle,color: 'text-blue-600',   bg: 'bg-blue-50' },
  MANUAL_DEBIT:    { label: 'Manual Debit',      icon: ArrowDownCircle,'color':'text-red-600',  bg: 'bg-red-50' },
  AI_DEBIT:        { label: 'AI Usage',          icon: Zap,          color: 'text-purple-600', bg: 'bg-purple-50' },
  AI_REFUND:       { label: 'AI Refund',         icon: RefreshCw,    color: 'text-teal-600',   bg: 'bg-teal-50' },
  ADJUSTMENT:      { label: 'Adjustment',        icon: AlertCircle,  color: 'text-slate-600',  bg: 'bg-slate-50' },
};

const TxnRow = ({ txn }) => {
  const meta = TXN_META[txn.transaction_type] || TXN_META.ADJUSTMENT;
  const Icon = meta.icon;
  const isPositive = txn.points_delta > 0;

  return (
    <div className="flex items-center gap-4 py-3 px-5 border-b border-slate-50 last:border-0 hover:bg-slate-50/50">
      <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${meta.bg}`}>
        <Icon className={`w-4 h-4 ${meta.color}`} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-slate-700">{meta.label}</span>
          {txn.action_type && txn.action_type !== 'SYSTEM' && txn.action_type !== 'ADMIN' && (
            <span className="px-1.5 py-0.5 text-xs bg-slate-100 text-slate-500 rounded">{txn.action_type}</span>
          )}
        </div>
        {txn.reason && (
          <p className="text-xs text-slate-400 truncate mt-0.5">{txn.reason}</p>
        )}
        <p className="text-xs text-slate-300 mt-0.5">
          {txn.created_at ? new Date(txn.created_at).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' }) : ''}
        </p>
      </div>
      <div className="text-right flex-shrink-0">
        <span className={`text-sm font-bold ${isPositive ? 'text-green-600' : 'text-red-500'}`}>
          {isPositive ? '+' : ''}{txn.points_delta}
        </span>
        <p className="text-xs text-slate-400">bal: {txn.balance_after}</p>
      </div>
    </div>
  );
};

// ─── Manual Adjustment Panel ───────────────────────────────────────────────────
const AdjustPanel = ({ customerId, onDone }) => {
  const [mode, setMode] = useState('credit');
  const [points, setPoints] = useState('');
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);

  const handleSubmit = async () => {
    const p = parseInt(points);
    if (!p || p <= 0) { toast.error('Enter valid points'); return; }
    setSaving(true);
    try {
      if (mode === 'credit') {
        await smartgateAPI.creditWallet(customerId, { points: p, reason });
        toast.success(`Credited ${p} points`);
      } else {
        await smartgateAPI.debitWallet(customerId, { points: p, reason });
        toast.success(`Debited ${p} points`);
      }
      setPoints('');
      setReason('');
      onDone();
    } catch (e) {
      toast.error(e.response?.data?.detail?.message || 'Failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-white rounded-xl border border-slate-200 p-5">
      <h3 className="font-semibold text-slate-800 mb-4">Manually Adjust Points</h3>
      <div className="flex gap-2 mb-4">
        {['credit', 'debit'].map(m => (
          <button
            key={m}
            onClick={() => setMode(m)}
            className={`flex-1 py-2 text-sm font-semibold rounded-lg transition-colors ${
              mode === m
                ? m === 'credit' ? 'bg-green-600 text-white' : 'bg-red-600 text-white'
                : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
            }`}
          >
            {m === 'credit' ? '+ Add Points' : '- Remove Points'}
          </button>
        ))}
      </div>
      <div className="space-y-3">
        <input
          type="number"
          value={points}
          onChange={e => setPoints(e.target.value)}
          min="1"
          placeholder="Points (e.g. 50)"
          data-testid="detail-adjust-points-input"
          className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
        <input
          type="text"
          value={reason}
          onChange={e => setReason(e.target.value)}
          placeholder="Reason (optional)"
          data-testid="detail-adjust-reason-input"
          className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
        <button
          onClick={handleSubmit}
          disabled={saving}
          data-testid="detail-adjust-submit-btn"
          className={`w-full py-2 text-sm font-semibold text-white rounded-lg transition-colors disabled:opacity-50 ${
            mode === 'credit' ? 'bg-green-600 hover:bg-green-700' : 'bg-red-600 hover:bg-red-700'
          }`}
        >
          {saving ? 'Saving...' : 'Confirm Adjustment'}
        </button>
      </div>
    </div>
  );
};

// ─── Main Page ─────────────────────────────────────────────────────────────────
export default function SmartGateWalletDetail() {
  const { customerId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [wallet, setWallet] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [txnTotal, setTxnTotal] = useState(0);
  const [txnPage, setTxnPage] = useState(1);
  const [txnLoading, setTxnLoading] = useState(false);

  const loadWallet = useCallback(async () => {
    try {
      const res = await smartgateAPI.getWallet(customerId);
      const { recent_transactions, ...w } = res.data;
      setWallet(w);
    } catch (e) {
      if (e.response?.status === 404) toast.error('Wallet not found');
      else toast.error('Failed to load wallet');
    } finally {
      setLoading(false);
    }
  }, [customerId]);

  const loadTransactions = useCallback(async (p = txnPage) => {
    setTxnLoading(true);
    try {
      const res = await smartgateAPI.getTransactions(customerId, { page: p, page_size: 20 });
      setTransactions(res.data.transactions || []);
      setTxnTotal(res.data.total || 0);
    } catch (e) {
      toast.error('Failed to load transactions');
    } finally {
      setTxnLoading(false);
    }
  }, [customerId, txnPage]);

  useEffect(() => { loadWallet(); loadTransactions(1); }, [customerId]); // eslint-disable-line

  const handleAdjustDone = () => {
    loadWallet();
    setTxnPage(1);
    loadTransactions(1);
  };

  if (loading) {
    return <div className="flex justify-center py-16"><RefreshCw className="w-6 h-6 animate-spin text-blue-500" /></div>;
  }

  if (!wallet) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-16 text-center">
        <AlertCircle className="w-12 h-12 text-slate-300 mx-auto mb-4" />
        <p className="text-slate-500">Wallet not found</p>
        <button onClick={() => navigate('/partners/smartgate/wallets')} className="mt-4 text-blue-600 text-sm hover:underline">Back to wallets</button>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-4 py-6 space-y-6" data-testid="wallet-detail-page">
      {/* Breadcrumb */}
      <div className="flex items-center gap-2 text-sm text-slate-400">
        <button onClick={() => navigate('/partners/smartgate')} className="flex items-center gap-1 hover:text-blue-600">
          <Shield className="w-4 h-4" /> SmartGate
        </button>
        <ChevronRight className="w-3 h-3" />
        <button onClick={() => navigate('/partners/smartgate/wallets')} className="flex items-center gap-1 hover:text-blue-600">
          <Users className="w-4 h-4" /> Wallets
        </button>
        <ChevronRight className="w-3 h-3" />
        <span className="font-mono text-slate-600 truncate max-w-xs">{customerId}</span>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left column: wallet summary + adjustment */}
        <div className="space-y-4">
          {/* Wallet summary card */}
          <div className="bg-gradient-to-br from-blue-600 to-blue-700 rounded-2xl p-5 text-white" data-testid="wallet-summary-card">
            <div className="flex items-center gap-2 mb-4">
              <User className="w-4 h-4 text-blue-200" />
              <span className="text-sm text-blue-200 font-mono truncate">{customerId}</span>
            </div>
            <div className="text-center py-2">
              <p className="text-5xl font-black">{wallet.points_balance ?? 0}</p>
              <p className="text-blue-200 text-sm mt-1">points balance</p>
            </div>
            <div className="mt-4 pt-4 border-t border-blue-500 grid grid-cols-2 gap-4 text-center">
              <div>
                <p className="text-lg font-bold">{wallet.total_points_earned ?? 0}</p>
                <p className="text-xs text-blue-200">total earned</p>
              </div>
              <div>
                <p className="text-lg font-bold">{wallet.total_points_spent ?? 0}</p>
                <p className="text-xs text-blue-200">total spent</p>
              </div>
            </div>
          </div>

          {/* Wallet meta */}
          <div className="bg-white rounded-xl border border-slate-200 p-4 space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-slate-400">Status</span>
              <span className={`font-semibold capitalize ${wallet.wallet_status === 'active' ? 'text-green-600' : 'text-red-500'}`}>
                {wallet.wallet_status}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-400">Created via</span>
              <span className="text-slate-600 capitalize">{(wallet.created_via || 'unknown').replace(/_/g, ' ')}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-400">Refunded</span>
              <span className="text-slate-600">{wallet.total_points_refunded ?? 0}</span>
            </div>
            {wallet.first_seen_at && (
              <div className="flex justify-between">
                <span className="text-slate-400">First seen</span>
                <span className="text-slate-600 text-xs">{new Date(wallet.first_seen_at).toLocaleDateString('en-IN')}</span>
              </div>
            )}
            {wallet.last_activity_at && (
              <div className="flex justify-between">
                <span className="text-slate-400">Last active</span>
                <span className="text-slate-600 text-xs">{new Date(wallet.last_activity_at).toLocaleDateString('en-IN')}</span>
              </div>
            )}
          </div>

          {/* Adjustment panel */}
          <AdjustPanel customerId={customerId} onDone={handleAdjustDone} />
        </div>

        {/* Right column: transaction history */}
        <div className="lg:col-span-2">
          <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
            <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Clock className="w-4 h-4 text-slate-400" />
                <h3 className="font-semibold text-slate-800">Transaction History</h3>
                <span className="text-xs text-slate-400">({txnTotal} total)</span>
              </div>
              <button onClick={() => { setTxnPage(1); loadTransactions(1); }} className="text-slate-400 hover:text-blue-600">
                <RefreshCw className="w-4 h-4" />
              </button>
            </div>

            {txnLoading ? (
              <div className="flex justify-center py-12"><RefreshCw className="w-5 h-5 animate-spin text-blue-500" /></div>
            ) : transactions.length === 0 ? (
              <div className="text-center py-12 text-slate-400">
                <Clock className="w-8 h-8 mx-auto mb-2 opacity-30" />
                <p className="text-sm">No transactions yet</p>
              </div>
            ) : (
              <div data-testid="transaction-list">
                {transactions.map(t => <TxnRow key={t.transaction_id} txn={t} />)}
              </div>
            )}

            {txnTotal > 20 && (
              <div className="px-5 py-3 border-t border-slate-100 flex items-center justify-between text-sm text-slate-500">
                <span>Page {txnPage} of {Math.ceil(txnTotal / 20)}</span>
                <div className="flex gap-2">
                  <button
                    disabled={txnPage === 1}
                    onClick={() => { const p = txnPage - 1; setTxnPage(p); loadTransactions(p); }}
                    className="px-3 py-1 rounded bg-slate-100 hover:bg-slate-200 disabled:opacity-40"
                  >Prev</button>
                  <button
                    disabled={txnPage * 20 >= txnTotal}
                    onClick={() => { const p = txnPage + 1; setTxnPage(p); loadTransactions(p); }}
                    className="px-3 py-1 rounded bg-slate-100 hover:bg-slate-200 disabled:opacity-40"
                  >Next</button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
